import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card'
import { cn } from '@/lib/utils'
import { LayoutGrid } from 'lucide-react'
import { trpc, type RouterOutput } from '@/config/trpc'
import { type PlatformType } from '@backend/lib/db/schema/platform'
import { HintLabel, InfoTooltip } from './info-tooltip'
import { PlatformGroupHeader } from './platform-group-header'

type FormatRow = RouterOutput['analytics']['formatImpact']['byFormat'][number]

const FORMAT_LABEL: Record<string, string> = {
  image: 'Image',
  video: 'Video',
  carousel: 'Carousel',
  reel: 'Reel',
  text: 'Text',
  document: 'Document',
  unknown: 'Other',
}

export function FormatImpactCard({ platforms }: { platforms: PlatformType[] }) {
  const { data, isLoading } = trpc.analytics.formatImpact.useQuery({
    platforms: platforms.length > 0 ? platforms : undefined,
  })

  // Group rows by platform — drives the grouped vs flat rendering decision.
  const grouped = data?.byFormat ? groupByPlatform(data.byFormat) : new Map()
  const isMultiPlatform = grouped.size > 1

  return (
    <Card className="relative overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <LayoutGrid size={14} className="text-muted-foreground" />
          Format Impact
          <InfoTooltip>
            Average engagement per post for each format you've used. Each platform is compared
            against its own baseline — Instagram reels vs. Instagram images, not vs. YouTube videos.
          </InfoTooltip>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && <SkeletonRows />}

        {data && !data.hasEnoughData && (
          <p className="text-muted-foreground py-4 text-xs">
            Need at least{' '}
            <span className="font-medium">
              {data.minFormatsPerPlatform} different formats on a platform
            </span>{' '}
            to compare. Post in more formats to unlock this view.
          </p>
        )}

        {data && data.hasEnoughData && (
          <div className={cn(isMultiPlatform ? 'space-y-4' : 'space-y-3')}>
            {[...grouped.entries()].map(([platform, rows]) => (
              <div key={platform}>
                {isMultiPlatform && <PlatformGroupHeader platform={platform} />}
                <FormatBars rows={rows} />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function FormatBars({ rows }: { rows: FormatRow[] }) {
  const max = Math.max(...rows.map((r) => r.avgEngagement))
  return (
    <div className="space-y-3">
      {rows.map((f) => {
        const widthPct = max > 0 ? (f.avgEngagement / max) * 100 : 0
        return (
          <div key={`${f.platform}-${f.mediaType}`}>
            <div className="mb-1 flex items-center justify-between text-xs">
              <span className="text-foreground font-medium">
                {FORMAT_LABEL[f.mediaType] ?? f.mediaType}
              </span>
              <span
                className={cn(
                  'font-medium',
                  f.isBaseline
                    ? 'text-muted-foreground'
                    : f.relativeBoost && f.relativeBoost > 0
                      ? 'text-emerald-600 dark:text-emerald-500'
                      : 'text-muted-foreground',
                )}>
                {f.isBaseline ? (
                  <HintLabel hint="Lowest-performing format on this platform. Others are shown relative to this one.">
                    baseline
                  </HintLabel>
                ) : f.relativeBoost != null ? (
                  `+${(f.relativeBoost * 100).toFixed(0)}%`
                ) : (
                  '—'
                )}
              </span>
            </div>
            <div className="bg-muted relative h-2 overflow-hidden rounded-full">
              <div
                className="bg-primary h-full rounded-full transition-all"
                style={{ width: `${widthPct}%` }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

function SkeletonRows() {
  return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => (
        <div key={i}>
          <div className="bg-muted mb-1 h-3 w-20 animate-pulse rounded" />
          <div className="bg-muted h-2 animate-pulse rounded-full" />
        </div>
      ))}
    </div>
  )
}

function groupByPlatform<T extends { platform: PlatformType }>(rows: T[]): Map<PlatformType, T[]> {
  const m = new Map<PlatformType, T[]>()
  for (const row of rows) {
    const list = m.get(row.platform) ?? []
    list.push(row)
    m.set(row.platform, list)
  }
  return m
}
